import React from 'react';
import type { ComponentType, ReactNode } from 'react';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { cn } from '@components/lib/utils';

/* ─────────────────────────────────────────────────────────────────────────
 *  Spinner — small inline indeterminate loader
 * ───────────────────────────────────────────────────────────────────────── */

export function Spinner({ className }: { className?: string }) {
  return (
    <Loader2
      className={cn('size-4 animate-spin text-muted-foreground', className)}
    />
  );
}

/* ─────────────────────────────────────────────────────────────────────────
 *  Shared composition for content states (Empty / Error / Loading)
 *  Centered icon (size-12) + bold title + muted description + optional CTA
 * ───────────────────────────────────────────────────────────────────────── */

type IconComponent = ComponentType<{ className?: string }>;

type StateProps = {
  icon?: IconComponent;
  title: string;
  description?: string;
  action?: ReactNode;
  className?: string;
  /** Tone affects icon color; not used for text. */
  tone?: 'default' | 'warning' | 'danger';
};

function ContentState({
  icon: Icon,
  title,
  description,
  action,
  className,
  tone = 'default',
}: StateProps) {
  const iconColor =
    tone === 'warning'
      ? 'text-marble-yellow-600'
      : tone === 'danger'
      ? 'text-marble-red-500'
      : 'text-marble-bg-400';

  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center gap-3 px-6 py-12 text-center',
        className,
      )}
    >
      {Icon && (
        <div
          className={cn(
            'flex size-12 items-center justify-center rounded-full bg-marble-bg-50',
            iconColor,
          )}
        >
          <Icon className="size-6" />
        </div>
      )}
      <div className="flex flex-col gap-1">
        <div className="text-lg font-bold text-foreground">{title}</div>
        {description && (
          <div className="max-w-sm text-sm text-muted-foreground">
            {description}
          </div>
        )}
      </div>
      {action && <div className="mt-2">{action}</div>}
    </div>
  );
}

export function EmptyState(props: StateProps) {
  return <ContentState {...props} />;
}

export function ErrorState(
  props: Omit<StateProps, 'tone' | 'icon'> & { icon?: IconComponent },
) {
  return (
    <ContentState {...props} icon={props.icon ?? AlertTriangle} tone="danger" />
  );
}

/* ─────────────────────────────────────────────────────────────────────────
 *  LoadingState — same composition shape but uses Spinner instead of icon
 * ───────────────────────────────────────────────────────────────────────── */

export function LoadingState({
  title = 'Loading…',
  description,
  className,
}: {
  title?: string;
  description?: string;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center gap-3 px-6 py-12 text-center',
        className,
      )}
    >
      <Spinner className="size-8 text-marble-blue-500" />
      <div className="flex flex-col gap-1">
        <div className="text-base font-semibold text-foreground">{title}</div>
        {description && (
          <div className="max-w-sm text-sm text-muted-foreground">
            {description}
          </div>
        )}
      </div>
    </div>
  );
}
